"use client";
import { getAlgoliaIndexName } from "@/apiUtils/algoliaUtils";
import { AlgoliaProductItem } from "@/apiUtils/algoliaUtils/types";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import { analyticEventTypes, ProductType } from "@/apiUtils/constants";
import { getProductNameAndMedia } from "@/apiUtils/productUtils";
import NoImageSrc from "@/assets/images/no_image.svg";
import { AvailablePromotion, MediaItem, TranslationData, VariantItem } from "@/common/lib/types";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { applicableProductTypes } from "../lcp/resource/config";
import ProductBadge from "../productBadge/ProductBadge";
import ProductCard from "./ProductCard";
import { generateAltText } from "@/utils/product.utils";
import { getLangFromLocale } from "@/utils/common";
import { hasArrayElements, extractProductColor, extractColorFromAttribute } from "@/apiUtils/commonUtils";

export interface IProps {
  currency?: string;
  product: AlgoliaProductItem & { objectID?: string; queryID?: string; indexName?: string };
  isChevronVariant?: boolean;
  index?: number;
  source?: string;
  sourceId?: string;
  translations: TranslationData;
  pageType?: string;
  showProductCarousel?: boolean;
  handleVisible?: (isVisible: boolean, data: AlgoliaProductItem) => void;
  tryOn: boolean;
  isNudgeAnimationPresent?: boolean;
  showCartAddedState?: boolean;
  onDeferToast?: (imgUrl: string) => void;
}

const ProductWithData = ({
  product,
  currency,
  isChevronVariant = true,
  index,
  translations,
  pageType,
  showProductCarousel = true,
  handleVisible = () => {},
  source,
  isNudgeAnimationPresent,
  sourceId,
  tryOn,
  showCartAddedState,
  onDeferToast,
}: IProps) => {
  const {
    variantColorMappings: productVariants,
    productType: { slug },
    media,
    brand,
    defaultVariant,
    queryID,
    objectID,
    indexName: productIndexName,
    hasMultipleFit,
  } = product;

  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);
  const indexName = productIndexName || getAlgoliaIndexName("", locale as string) || "";

  const defaultVariantIn = useMemo((): VariantItem => {
    let variantItem =
      productVariants?.find(
        (item: VariantItem) => item?.variantId === defaultVariant?.variantId,
      ) ?? productVariants?.[0];
    return variantItem;
  }, [productVariants, defaultVariant]);

  const [selectedVariant, setSelectedVariant] = useState<VariantItem>(defaultVariantIn);
  const [isDefaultVariant, setIsDefaultVariant] = useState<boolean>(true);

  useEffect(() => {
    setSelectedVariant(defaultVariantIn);
  }, [defaultVariantIn]);

  const handleSelectVariant = async (item: VariantItem) => {
    setIsDefaultVariant(false);
    setSelectedVariant(item);
  };

  const handleOnComplete = () => {
    handlePostAnalytics({
      eventType: analyticEventTypes.ACTIONS,
      data: {
        target: analyticEvents.ADD_TO_WISHLIST,
        product: { ...product, hitIndex: index, selectedQuantity: 1 },
        selectedVariantId: selectedVariant?.variantId,
      },
    });
  };
  const { productMedia, productName }: { productMedia: MediaItem[]; productName: string } = useMemo(
    () => getProductNameAndMedia(product, selectedVariant, isDefaultVariant),
    [product, selectedVariant, isDefaultVariant],
  );

  const badge = useMemo(() => {
    return selectedVariant?.availableBadges?.length && selectedVariant?.availableBadges?.length > 0
      ? selectedVariant?.availableBadges[0]
      : undefined;
  }, [selectedVariant]);

  const promotionalDetails: AvailablePromotion[] = useMemo(() => {
    const promotionDetailsIn =
      selectedVariant?.availablePromotion?.length && selectedVariant?.availablePromotion?.length > 0
        ? selectedVariant?.availablePromotion.slice(0, 2)
        : undefined;
    return promotionDetailsIn || [];
  }, [selectedVariant]);

  const ProductImages = hasArrayElements(product?.media)
    ? product?.media.map((img: { url: string }) => {
        return { url: getCloudinaryURL(img?.url) ?? NoImageSrc.src };
      })
    : productMedia.map((img) => {
        return {
          type: img.type,
          // url: `https://mcdn.magrabi.com/image/upload/c_limit,w_384/f_auto/q_95/v1/p/prod/i${img.url}`,
          url: getCloudinaryURL(img.url) ?? NoImageSrc.src,
        };
      });

  const isInstantAddToCartEnabled = useMemo(
    () => ![ProductType.CL_CLEAR, ProductType.CL_COLOR, ...applicableProductTypes].includes(slug),
    [slug],
  );
// MEPS-987 Added `selectedVariant?.inStock === false` check to fix badge visibility:the `inStock` flag is available on the selected variant
  const isOutOfStock =
    selectedVariant?.inStock === false
      ? true
      : defaultVariant?.inStock !== undefined
        ? !defaultVariant.inStock
        : product.isAvailableForPurchase !== undefined
          ? !product.isAvailableForPurchase
          : false;

  // Helper function to extract color from nested attribute structure

  // Extract product color using utility function
  const { color: productColor, source: colorSource } = extractProductColor(product, selectedVariant, lang);

  
  // Extract swatches color (for Pair Eyewear products)
  const swatchesColor = 
    extractColorFromAttribute(selectedVariant?.attributes?.[lang]?.swatches) ||
    extractColorFromAttribute(selectedVariant?.attributes?.swatches) ||
    extractColorFromAttribute(product?.defaultVariant?.attributes?.[lang]?.swatches) ||
    extractColorFromAttribute(product?.defaultVariant?.attributes?.swatches) ||
    extractColorFromAttribute(product?.attributes?.[lang]?.swatches) ||
    extractColorFromAttribute(product?.attributes?.swatches) ||
    "";
  
  const altText = generateAltText(
    brand?.name || product?.defaultBrandName || "",
    productName || "",
    productColor,
    swatchesColor,
    lang,
  );

  return (
    <>
      <ProductCard
        translations={translations}
        addToCart={isInstantAddToCartEnabled}
        productLink={selectedVariant?.url}
        productImages={ProductImages}
        slug={slug}
        productImageStyles={{ height: 300, width: 300 }}
        tryOn={selectedVariant?.vtoEnabled === "true" && tryOn}
        name={brand?.name || product?.defaultBrandName || ""}
        description={productName || ""}
        pricing={selectedVariant?.pricing}
        ean={product?.defaultVariant?.attributes?.sapEAN}
        sku={product?.defaultVariant?.sku}
        productTileBadge={
          <ProductBadge
            variant={isOutOfStock ? "outOfStock" : (badge?.name ?? "")}
            title={
              isOutOfStock
                ? (translations?.plp?.plp_out_of_stock_label ?? "Out of Stock")
                : (badge?.name ?? "")
            }
          />
        }
        productVariants={productVariants || []}
        promotionalDetails={promotionalDetails}
        handleSelectVariant={handleSelectVariant}
        selectedVariant={selectedVariant}
        isChevronVariant={isChevronVariant}
        isDefaultVariant={isDefaultVariant}
        onComplete={handleOnComplete}
        showProductCarousel={showProductCarousel}
        pageType={pageType}
        queryID={queryID || ""}
        objectID={objectID || selectedVariant?.variantId || ""}
        indexName={indexName}
        index={index}
        onVisible={(isVisible) => handleVisible(isVisible, product)}
        source={source}
        sourceId={sourceId}
        isNudgeAnimationPresent={isNudgeAnimationPresent}
        altText={altText}
        productItem={product}
        showCartAddedState={showCartAddedState}
        onDeferToast={onDeferToast}
      />
    </>
  );
};

export default ProductWithData;
